import * as React from "react"
import { Link, usePage } from "@inertiajs/react"
import { ArrowRight, Menu, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const links = [
  { href: "/features", label: "Features" },
  { href: "/brand", label: "System" },
]

export function MarketingShell({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false)
  const { url } = usePage()

  React.useEffect(() => setOpen(false), [url])

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <a className="skip-link" href="#marketing-content">Skip to content</a>
      <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-5 sm:pt-5">
        <nav className="mx-auto flex h-14 max-w-[90rem] items-center justify-between rounded-xl border bg-background/92 px-3 shadow-xs backdrop-blur-xl sm:px-4" aria-label="Primary navigation">
          <Link href="/" className="flex items-center gap-2.5 text-foreground no-underline hover:text-foreground" aria-label="Contracting OS home">
            <BrandMark />
            <span className="text-sm font-semibold tracking-tight">Contracting OS</span>
          </Link>
          <div className="hidden items-center gap-1 md:flex">
            {links.map((link) => (
              <Link key={link.href} href={link.href} className={cn("rounded-md px-3 py-2 text-sm text-muted-foreground no-underline transition-colors hover:bg-accent hover:text-accent-foreground", url === link.href && "bg-accent text-accent-foreground")}>
                {link.label}
              </Link>
            ))}
          </div>
          <div className="hidden items-center gap-2 md:flex">
            <Button variant="ghost" size="sm" asChild><Link href="/login">Log in</Link></Button>
            <Button size="sm" asChild><Link href="/signup">Start workspace <ArrowRight className="size-4" /></Link></Button>
          </div>
          <button type="button" className="inline-flex size-9 items-center justify-center rounded-md border md:hidden" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="marketing-mobile-nav" aria-label={open ? "Close navigation" : "Open navigation"}>
            {open ? <X className="size-4" /> : <Menu className="size-4" />}
          </button>
        </nav>
        {open && (
          <div id="marketing-mobile-nav" className="mx-auto mt-2 max-w-[90rem] rounded-xl border bg-background p-3 shadow-xl md:hidden">
            <div className="flex flex-col">
              {links.map((link) => <Link key={link.href} href={link.href} className="rounded-md px-3 py-3 text-sm text-foreground no-underline hover:bg-accent">{link.label}</Link>)}
              <Link href="/login" className="rounded-md px-3 py-3 text-sm text-foreground no-underline hover:bg-accent">Log in</Link>
              <Button className="mt-2" asChild><Link href="/signup">Start workspace</Link></Button>
            </div>
          </div>
        )}
      </header>
      <main id="marketing-content" className="w-full max-w-full overflow-x-hidden" tabIndex={-1}>{children}</main>
      <MarketingFooter />
    </div>
  )
}

export function BrandMark({ inverse = false }: { inverse?: boolean }) {
  return (
    <span className={cn("relative grid size-8 place-items-center rounded-md", inverse ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground")} aria-hidden>
      <span className="absolute left-2 top-2 h-3 w-px bg-current" />
      <span className="absolute left-2 top-2 h-px w-4 bg-current" />
      <span className="absolute bottom-2 right-2 h-3 w-px bg-current" />
      <span className="absolute bottom-2 right-2 h-px w-4 bg-current" />
    </span>
  )
}

function MarketingFooter() {
  return (
    <footer className="border-t bg-background px-5 py-10 sm:px-8 lg:px-12">
      <div className="mx-auto flex max-w-[90rem] flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
        <div><div className="flex items-center gap-2.5"><BrandMark /><span className="text-sm font-semibold">Contracting OS</span></div><p className="mt-4 max-w-md text-sm text-muted-foreground">Commercial scope, site evidence, and project value connected by one traceable thread.</p></div>
        <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
          <Link href="/features" className="text-muted-foreground no-underline hover:text-foreground">Features</Link>
          <Link href="/brand" className="text-muted-foreground no-underline hover:text-foreground">System</Link>
          <Link href="/login" className="text-muted-foreground no-underline hover:text-foreground">Log in</Link>
        </div>
      </div>
    </footer>
  )
}

export function MarketingCta({ title, body }: { title: React.ReactNode; body: string }) {
  return (
    <section className="border-t bg-foreground px-5 py-24 text-background sm:px-8 md:py-32 lg:px-12">
      <div className="mx-auto grid max-w-[90rem] gap-10 lg:grid-cols-[1fr_auto] lg:items-end">
        <div><h2 className="max-w-5xl text-[clamp(3rem,6.5vw,7rem)] font-semibold leading-[.9] tracking-[-0.065em] text-background">{title}</h2><p className="mt-7 max-w-2xl text-lg text-background/60">{body}</p></div>
        <Button size="lg" asChild><Link href="/signup">Create workspace <ArrowRight className="size-4" /></Link></Button>
      </div>
    </section>
  )
}
